import React from "react"

import { DrawerBody, DrawerHeader, DrawerTitle, SearchInput } from "@coval/ui"

import {
  DetailRow,
  formatValue,
  isNonEmptyJson,
  JsonBlock,
  KeyValueList,
  sortEntriesByKey,
} from "@/components/detail/DetailFields"
import type { JsonObject } from "@/types/json"

/**
 * What a conversation carried at run time, as opposed to what the test case
 * that started it defined. A monitored conversation has this and no test case
 * at all, which is why it is held apart from `TestCaseData`.
 */
export interface ConversationMetadata {
  /** Metadata submitted with the conversation, stored as run.customer_metadata. */
  customerMetadata?: JsonObject | null
  /** What presimulation initialization returned for this conversation. */
  initializationOutput?: JsonObject | null
  /** Attributes presimulation resolved before the conversation ran. */
  externalSimulationAttributes?: JsonObject | null
}

/** Whether a conversation carries any metadata worth opening a panel for. */
export function hasConversationMetadata(
  metadata: ConversationMetadata | null | undefined,
): boolean {
  if (!metadata) {
    return false
  }
  return (
    isNonEmptyJson(metadata.customerMetadata) ||
    isNonEmptyJson(metadata.initializationOutput) ||
    isNonEmptyJson(metadata.externalSimulationAttributes)
  )
}

/** How many fields the metadata sets, for a card that summarizes it. */
export function countConversationMetadataKeys(
  metadata: ConversationMetadata | null | undefined,
): number {
  if (!metadata) {
    return 0
  }
  return (
    Object.keys(metadata.customerMetadata ?? {}).length +
    Object.keys(metadata.initializationOutput ?? {}).length +
    Object.keys(metadata.externalSimulationAttributes ?? {}).length
  )
}

/** At this many fields the panel offers a search box. */
const SEARCHABLE_KEY_COUNT = 8

/** Whether a field's key or value text contains the query, ignoring case. */
function entryMatches([key, value]: [string, unknown], query: string): boolean {
  return key.toLowerCase().includes(query) || jsonMatches(value, query)
}

/**
 * Whether any key or scalar value anywhere in a JSON value contains the query.
 * Walks the structure rather than its serialized text, so the query never
 * matches quotes, braces, or other syntax.
 */
function jsonMatches(value: unknown, query: string): boolean {
  if (value == null) {
    return false
  }
  if (Array.isArray(value)) {
    return value.some((item) => jsonMatches(item, query))
  }
  if (typeof value === "object") {
    return Object.entries(value).some(
      ([key, item]) =>
        key.toLowerCase().includes(query) || jsonMatches(item, query),
    )
  }
  return formatValue(value)?.toLowerCase().includes(query) ?? false
}

/** A field set's entries in key order, narrowed to those matching the query. */
function visibleEntries(
  value: JsonObject | null | undefined,
  query: string,
): [string, unknown][] {
  const entries = sortEntriesByKey(Object.entries(value ?? {}))
  return query ? entries.filter((entry) => entryMatches(entry, query)) : entries
}

/** A conversation's own metadata, read out as detail rows. */
const ConversationMetadataDetails: React.FC<{
  metadata: ConversationMetadata
  query: string
}> = ({ metadata, query }) => {
  const customerEntries = visibleEntries(metadata.customerMetadata, query)
  const initializationEntries = visibleEntries(
    metadata.initializationOutput,
    query,
  )
  const showExternalAttributes =
    isNonEmptyJson(metadata.externalSimulationAttributes) &&
    (!query || jsonMatches(metadata.externalSimulationAttributes, query))

  if (
    customerEntries.length === 0 &&
    initializationEntries.length === 0 &&
    !showExternalAttributes
  ) {
    return (
      <p className="text-coval-text-muted text-sm">
        No metadata matches this search.
      </p>
    )
  }

  return (
    <dl className="space-y-5 pt-2">
      {customerEntries.length > 0 ? (
        <DetailRow label="Custom metadata">
          <KeyValueList entries={customerEntries} />
        </DetailRow>
      ) : null}

      {initializationEntries.length > 0 ? (
        <DetailRow label="Initialization Output">
          <KeyValueList entries={initializationEntries} />
        </DetailRow>
      ) : null}

      {showExternalAttributes ? (
        <DetailRow label="External Attributes from Presimulation">
          <JsonBlock value={metadata.externalSimulationAttributes} />
        </DetailRow>
      ) : null}
    </dl>
  )
}

/**
 * The drawer panel for a conversation's metadata. It reads the same fields in
 * a monitored conversation and a simulation result, so both surfaces reach
 * their metadata the same way rather than one of them routing through the test
 * set that happened to start it. Fields read in key order, and a record with
 * many of them gets a search box that matches keys and values.
 */
export const ConversationMetadataPanel: React.FC<{
  metadata: ConversationMetadata
}> = ({ metadata }) => {
  const [search, setSearch] = React.useState("")
  const searchable =
    countConversationMetadataKeys(metadata) >= SEARCHABLE_KEY_COUNT
  const query = searchable ? search.trim().toLowerCase() : ""

  return (
    <>
      <DrawerHeader>
        <DrawerTitle>Metadata</DrawerTitle>
      </DrawerHeader>
      {searchable ? (
        <div className="shrink-0 border-b border-coval-border px-4 py-2">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch("")}
            placeholder="Search keys and values..."
            aria-label="Search metadata"
          />
        </div>
      ) : null}
      <DrawerBody>
        {hasConversationMetadata(metadata) ? (
          <ConversationMetadataDetails metadata={metadata} query={query} />
        ) : (
          <p className="text-coval-text-muted text-sm">
            This conversation carries no metadata.
          </p>
        )}
      </DrawerBody>
    </>
  )
}
